// SearchModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SearchModal.module.css';

import { useLazySearchAllUsersQuery } from '../store/api/searchApi';
import { useGetUsersByFollowersQuery } from '../store/api/followApi';

export default function SearchModal({ isOpen, onClose }) {

  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // search API
  const [
    searchAllUsers,
    { currentData: results = [], isLoading }
  ] = useLazySearchAllUsersQuery();

  // suggested users API
  const { data: suggestedUsers = [] } = useGetUsersByFollowersQuery();

  const limitedSuggestedUsers = suggestedUsers.slice(0, 6);

  /* -------------------- Debounce -------------------- */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [query]);

  /* -------------------- API Call -------------------- */
  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      searchAllUsers(
        { query: debouncedQuery },
        { preferCacheValue: false }
      );
    }
  }, [debouncedQuery, searchAllUsers]);

  /* -------------------- Handlers -------------------- */
  const handleSearchChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  const handleClose = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
    onClose();
  }, [onClose]);

  const handleUserClick = useCallback((userId) => {
    navigate(`/profile/${userId}`);
    handleClose();
  }, [navigate, handleClose]);

  const handleSeeAll = () => {
    navigate('/suggested-users');
    handleClose();
  };

  /* -------------------- Render Guard -------------------- */
  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.header}>
          <h2>Search People</h2>
          <button className={styles.closeBtn} onClick={handleClose}>×</button>
        </div>

        {/* Search Input */}
        <input
          type="text"
          placeholder="Search people, founders, investors..."
          value={query}
          onChange={handleSearchChange}
          autoFocus
          className={styles.searchInput}
        />

        {/* Results */}
        <div className={styles.results}>

          {/* Suggested Users */}
          {!query && suggestedUsers.length > 0 && (
            <div className={styles.resultsList}>

              <div className={styles.suggestedHeader}>
                <h3>Suggested Users</h3>

                {suggestedUsers.length > 6 && (
                  <span
                    className={styles.seeAll}
                    onClick={handleSeeAll}
                  >
                    See All
                  </span>
                )}
              </div>

              {limitedSuggestedUsers.map(user => (
                <div
                  key={user.id}
                  className={styles.userCard}
                  onClick={() => handleUserClick(user.id)}
                >
                  {user.profilePhotoUrl ? (
                    <img
                      src={user.profilePhotoUrl}
                      alt={user.name}
                      className={styles.avatar}
                    />
                  ) : (
                    <div className={styles.avatarPlaceholder}>
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                  )}

                  <div className={styles.userInfo}>
                    <h3>{user.name}</h3>

                    {user.userType && (
                      <p className={styles.userType}>
                        {user.userType}{user.userType === 'Rookie' && user.rookieType
              ? ` (${user.rookieType})`
              : ''}
                      </p>
                    )}
                    <p className={styles.followers}>
                      {user.state}
                    </p>
                  </div>
                </div>
              ))}

            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Searching...</p>
            </div>
          )}

          {/* Search Results */}
          {debouncedQuery.length >= 2 && !isLoading && results.length > 0 && (
            <div className={styles.resultsList}>
              {results.map(user => (
                <div
                  key={user.id}
                  className={styles.userCard}
                  onClick={() => handleUserClick(user.id)}
                >
                  {user.profilePhotoUrl ? (
                    <img
                      src={user.profilePhotoUrl}
                      alt={user.name}
                      className={styles.avatar}
                    />
                  ) : (
                    <div className={styles.avatarPlaceholder}>
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                  )}

                  <div className={styles.userInfo}>
                    <h3>{user.name}</h3>

                    {user.userType && (
                      <p className={styles.userType}>
                        {user.userType}
                      </p>
                    )}

                    <p className={styles.followers}>
                      {user.followersCount} followers
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {debouncedQuery.length >= 2 && !isLoading && results.length === 0 && (
            <div className={styles.emptyState}>
              <p>No users found for "{debouncedQuery}"</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}